import React, { useId } from 'react';
import { CircleAlert, CircleCheck, Info, LucideIcon, TriangleAlert } from 'lucide-react';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader } from '../ui';

type MessageType = 'error' | 'warning' | 'info' | 'success';

type Props = {
  open: boolean;
  title?: string;
  message: React.ReactNode;
  onClose: () => void;
  buttonText?: string;
  type?: MessageType;
};

const ICONS: Record<MessageType, { icon: LucideIcon; className: string }> = {
  error: { icon: CircleAlert, className: 'text-primary-600' },
  warning: { icon: TriangleAlert, className: 'text-amber-500' },
  success: { icon: CircleCheck, className: 'text-emerald-500' },
  info: { icon: Info, className: 'text-neutral-400' },
};

export const MessageModal: React.FC<Props> = ({
  open,
  title = 'Notice',
  message,
  onClose,
  buttonText = 'OK',
  type = 'info',
}) => {
  const titleId = useId();
  const { icon: Icon, className } = ICONS[type] ?? ICONS.info;

  return (
    <Dialog open={open} onClose={onClose} labelledBy={titleId} handle className="sm:max-w-md">
      <DialogHeader
        id={titleId}
        className="max-sm:pt-1"
        title={
          <span className="flex items-center gap-2">
            <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${className}`} strokeWidth={1.75} aria-hidden="true" />
            {title}
          </span>
        }
      />
      <DialogBody className="pb-5 pt-2 text-[13px] leading-relaxed text-neutral-600">{message}</DialogBody>
      <DialogFooter>
        <Button variant="dark" onClick={onClose} className="h-11 flex-1 sm:h-9 sm:flex-none sm:px-5">
          {buttonText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
